import React, { useMemo, useState } from 'react';
import { Layers, Palette, Pencil, Plus, Trash2 } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import { FormDrawer } from '../forms';
import { useUIColors } from '../../hooks/useTheme';
import ProductionDataTable from './ProductionDataTable';
import ProductionStatusChip from './ProductionStatusChip';
import PaintFormulaDetailModal from './PaintFormulaDetailModal';
import useProductionStore from './useProductionStore';
import {
  emptyFormula,
  emptyFormulaLine,
  FORMULA_COMPONENT_UNITS,
  FORMULA_COST_BASIS_OPTIONS,
  FORMULA_KIND_BASE,
  FORMULA_KIND_COLOR,
  FORMULA_KIND_LABELS,
  formulaLineCostBreakdown,
  formulaMassPercentages,
  getActiveFormulaStatuses,
  getFormulaStatusColor,
  formatDateEs,
  formatQty,
  formulaKindLabel,
  nextFormulaCode,
  normalizeFormulaKind,
  PAINT_PRODUCT_OPTIONS,
  productLabel,
} from './productionDomain';
import {
  activeMaterials,
  emptyTechParam,
  formatMoneyClp,
  getTechParamSuggestions,
  sanitizeTechParams,
  techParamRangeError,
  upsertTechParamSuggestion,
} from './rawMaterialsDomain';

const cell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: 'none',
  background: '#16a34a',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const cellInput = (baseInputStyle) => ({
  ...baseInputStyle,
  height: 32,
  padding: '0 8px',
  fontSize: 12,
});

const FormulaTechParamsTable = ({
  rows = [],
  onChange,
  colors,
  baseInputStyle,
  suggestions = [],
  onSuggestionsChange,
}) => {
  const listId = 'formula-tech-param-suggestions';
  const [showNewParam, setShowNewParam] = useState(false);
  const [newParamName, setNewParamName] = useState('');
  const [newParamUnit, setNewParamUnit] = useState('');
  const [paramHint, setParamHint] = useState('');

  const updateRow = (index, patch) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };
  const applySuggestion = (index, name) => {
    const preset = suggestions.find((item) => item.name === name);
    updateRow(index, { name, unit: preset?.unit ?? rows[index]?.unit ?? '' });
  };
  const persistSuggestion = (name, unit) => {
    const result = upsertTechParamSuggestion({ name, unit });
    if (!result.ok) {
      setParamHint(result.error || 'No se pudo guardar el parámetro.');
      return result;
    }
    onSuggestionsChange?.();
    setParamHint(
      result.existing
        ? `“${result.name}” ya estaba en la lista.`
        : `“${result.name}” se agregó a la lista desplegable.`
    );
    return result;
  };
  const handleCreateParamType = () => {
    const result = persistSuggestion(newParamName, newParamUnit);
    if (!result.ok) return;
    onChange([
      ...rows,
      {
        ...emptyTechParam(),
        name: result.name,
        unit: result.unit || newParamUnit || '',
      },
    ]);
    setNewParamName('');
    setNewParamUnit('');
    setShowNewParam(false);
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <datalist id={listId}>
        {suggestions.map((item) => (
          <option key={item.name} value={item.name} />
        ))}
      </datalist>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead>
            <tr>
              {['Parámetro', 'Unidad', 'Valor ideal', 'Mínimo', 'Máximo', ''].map((header) => (
                <th
                  key={header || 'actions'}
                  style={{
                    textAlign: header ? 'left' : 'right',
                    padding: '6px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.muted,
                    borderBottom: `1px solid ${colors.sectionBorder}`,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row, index) => (
                <tr key={row.key || index}>
                  <td style={{ padding: '6px 6px 6px 0' }}>
                    <input
                      value={row.name || ''}
                      list={listId}
                      placeholder="Densidad, VOC…"
                      onChange={(event) => applySuggestion(index, event.target.value)}
                      onBlur={() => {
                        if (String(row.name || '').trim()) {
                          persistSuggestion(row.name, row.unit);
                        }
                      }}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.unit || ''}
                      placeholder="g/cm³"
                      onChange={(event) => updateRow(index, { unit: event.target.value })}
                      onBlur={() => {
                        if (String(row.name || '').trim()) {
                          persistSuggestion(row.name, row.unit);
                        }
                      }}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 100 }}>
                    <input
                      value={row.ideal ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { ideal: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.min ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { min: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      value={row.max ?? ''}
                      placeholder="—"
                      onChange={(event) => updateRow(index, { max: event.target.value })}
                      style={cellInput(baseInputStyle)}
                    />
                  </td>
                  <td style={{ padding: '6px 0 6px 6px', width: 36, textAlign: 'right' }}>
                    <button
                      type="button"
                      aria-label="Quitar parámetro"
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                      style={{
                        height: 32,
                        width: 32,
                        border: 'none',
                        background: 'transparent',
                        color: colors.danger || '#dc2626',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ padding: '14px 8px', fontSize: 12, color: colors.muted }}>
                  Sin parámetros. Agrega densidad, sólidos, VOC u otros rangos de control.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => onChange([...rows, emptyTechParam()])}
          style={{
            ...primaryBtn,
            height: 32,
            background: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.inputBorder}`,
          }}
        >
          <Plus size={14} /> Agregar Fila
        </button>
        <button
          type="button"
          onClick={() => {
            setShowNewParam((prev) => !prev);
            setParamHint('');
          }}
          style={{
            ...primaryBtn,
            height: 32,
            background: 'transparent',
            color: colors.text,
            border: `1px solid ${colors.inputBorder}`,
          }}
        >
          <Plus size={14} /> Agregar Parámetro
        </button>
      </div>
      {showNewParam ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(90px, 0.6fr) auto',
            gap: 8,
            alignItems: 'end',
            padding: 10,
            borderRadius: 8,
            border: `1px solid ${colors.inputBorder}`,
            background: colors.panel || 'transparent',
          }}
        >
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.muted }}>Nombre del parámetro</span>
            <input
              value={newParamName}
              onChange={(event) => setNewParamName(event.target.value)}
              placeholder="Ej. Viscosidad…"
              style={cellInput(baseInputStyle)}
            />
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.muted }}>Unidad</span>
            <input
              value={newParamUnit}
              onChange={(event) => setNewParamUnit(event.target.value)}
              placeholder="—, %, °C…"
              style={cellInput(baseInputStyle)}
            />
          </label>
          <button type="button" onClick={handleCreateParamType} style={{ ...primaryBtn, height: 32 }}>
            Guardar en lista
          </button>
        </div>
      ) : null}
      {paramHint ? (
        <p style={{ margin: 0, fontSize: 11, color: colors.muted }}>{paramHint}</p>
      ) : null}
    </div>
  );
};

const BomEditor = ({
  value = [],
  onChange,
  materials,
  bases = [],
  showBases = false,
  formula,
  onFormulaMetaChange,
  materialByCodeMap,
  colors,
  baseInputStyle,
}) => {
  const commonUnit = String(formula?.component_unit || 'kg').trim() || 'kg';
  const costBasis = String(formula?.cost_basis || 'L').trim() === 'gl' ? 'gl' : 'L';
  const lines = Array.isArray(value) && value.length ? value : [emptyFormulaLine(commonUnit)];
  const massPercents = formulaMassPercentages(lines, materialByCodeMap);
  const costBasisLabel = costBasis === 'gl' ? 'gl' : 'L';

  const updateLine = (index, patch) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const setCommonUnit = (unit) => {
    onFormulaMetaChange?.('component_unit', unit);
    onChange(lines.map((line) => ({ ...line, unit })));
  };

  const fillComponent = (index, code) => {
    const base = bases.find((row) => row.code === code);
    if (base) {
      updateLine(index, {
        item_code: code,
        item_name: base.name || base.product_name || base.code,
        kind: 'Base',
        unit: commonUnit,
      });
      return;
    }
    const material = materials.find((row) => row.code === code);
    updateLine(index, {
      item_code: code,
      item_name: material?.name || '',
      kind: 'Materias primas',
      unit: commonUnit,
    });
  };

  const totalContribution = lines.reduce((sum, line) => {
    const { contribution } = formulaLineCostBreakdown(line, formula, materialByCodeMap);
    return sum + contribution;
  }, 0);

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(140px, 1fr) minmax(160px, 1fr)',
          gap: 10,
          alignItems: 'end',
        }}
      >
        <label style={{ fontSize: 11, color: colors.text, display: 'grid', gap: 4 }}>
          Unidad común de componentes
          <select
            value={commonUnit}
            onChange={(event) => setCommonUnit(event.target.value)}
            style={baseInputStyle}
          >
            {FORMULA_COMPONENT_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
            {commonUnit && !FORMULA_COMPONENT_UNITS.includes(commonUnit) ? (
              <option value={commonUnit}>{commonUnit}</option>
            ) : null}
          </select>
        </label>
        <label style={{ fontSize: 11, color: colors.text, display: 'grid', gap: 4 }}>
          Base de normalización de costos
          <select
            value={costBasis}
            onChange={(event) => onFormulaMetaChange?.('cost_basis', event.target.value)}
            style={baseInputStyle}
          >
            {FORMULA_COST_BASIS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
          <thead>
            <tr>
              {[
                'Componente',
                'Cantidad',
                'Unidad',
                '% m/m',
                'Costo unit.',
                `Costo / ${costBasisLabel}`,
                'Merma %',
                '',
              ].map((header) => (
                <th
                  key={header || 'actions'}
                  style={{
                    textAlign: header === 'Componente' || !header ? 'left' : 'right',
                    padding: '6px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.muted,
                    borderBottom: `1px solid ${colors.sectionBorder}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const { unitCost, contribution } = formulaLineCostBreakdown(
                line,
                formula,
                materialByCodeMap
              );
              const material = materialByCodeMap[String(line.item_code || '').trim()];
              const linked =
                Boolean(material) &&
                (Boolean(String(material.procurement_code || '').trim()) ||
                  Boolean(material.procurement_item_id) ||
                  Number(material.unit_cost) > 0 ||
                  Number(material.acquisition_unit_cost) > 0);
              const mm = massPercents[index] || 0;
              return (
                <tr key={`bom-${index}`}>
                  <td style={{ padding: '6px 6px 6px 0', minWidth: 220 }}>
                    <select
                      value={line.item_code || ''}
                      onChange={(event) => fillComponent(index, event.target.value)}
                      style={cellInput(baseInputStyle)}
                    >
                      <option value="">Seleccionar…</option>
                      {showBases && bases.length ? (
                        <optgroup label="Bases activas">
                          {bases.map((base) => (
                            <option key={base.code} value={base.code}>
                              {base.code} · {base.name || base.product_name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                      <optgroup label="Materias primas">
                        {materials.map((materialRow) => (
                          <option key={materialRow.code} value={materialRow.code}>
                            {materialRow.code}
                            {materialRow.procurement_code
                              ? ` · ${materialRow.procurement_code}`
                              : ''}{' '}
                            · {materialRow.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </td>
                  <td style={{ padding: 6, width: 88 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.quantity}
                      onChange={(event) => updateLine(index, { quantity: event.target.value })}
                      style={{ ...cellInput(baseInputStyle), textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: 6, width: 64, textAlign: 'right', fontSize: 12, color: colors.muted }}>
                    {line.unit || commonUnit}
                  </td>
                  <td style={{ padding: 6, width: 72, textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    {Number(line.quantity) > 0 ? `${mm.toFixed(2)}%` : '—'}
                  </td>
                  <td
                    style={{
                      padding: 6,
                      width: 96,
                      textAlign: 'right',
                      fontSize: 12,
                      color: linked ? colors.text : colors.muted,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    title={
                      linked
                        ? material?.procurement_code
                          ? `Catálogo: ${material.procurement_code}`
                          : 'Costo de materia prima'
                        : 'Sin vínculo a catálogo de adquisiciones'
                    }
                  >
                    {linked && unitCost > 0 ? formatMoneyClp(unitCost) : '—'}
                  </td>
                  <td
                    style={{
                      padding: 6,
                      width: 100,
                      textAlign: 'right',
                      fontSize: 12,
                      fontWeight: linked && contribution > 0 ? 600 : 400,
                      color: linked ? colors.text : colors.muted,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {linked && contribution > 0 ? formatMoneyClp(contribution) : '—'}
                  </td>
                  <td style={{ padding: 6, width: 72 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={line.scrap_pct}
                      onChange={(event) => updateLine(index, { scrap_pct: event.target.value })}
                      style={{ ...cellInput(baseInputStyle), textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '6px 0 6px 6px', width: 36, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => onChange(lines.filter((_, i) => i !== index))}
                      disabled={lines.length <= 1}
                      style={{
                        height: 32,
                        width: 32,
                        border: 'none',
                        background: 'transparent',
                        color: colors.danger || '#dc2626',
                        cursor: lines.length <= 1 ? 'not-allowed' : 'pointer',
                      }}
                      aria-label="Quitar componente"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {totalContribution > 0 ? (
            <tfoot>
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: '10px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.muted,
                    textAlign: 'right',
                    borderTop: `1px solid ${colors.sectionBorder}`,
                  }}
                >
                  Costo total / {costBasisLabel}
                </td>
                <td
                  style={{
                    padding: '10px 8px',
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: 'right',
                    borderTop: `1px solid ${colors.sectionBorder}`,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatMoneyClp(totalContribution)}
                </td>
                <td colSpan={2} style={{ borderTop: `1px solid ${colors.sectionBorder}` }} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: colors.muted }}>
        % m/m sobre la masa total de componentes. El costo unitario y el aporte aparecen cuando el
        componente está vinculado a materia prima / catálogo de adquisiciones. La normalización usa el
        rendimiento de la fórmula ({formatQty(formula?.yield_qty, formula?.yield_unit)}).
      </p>
      <button
        type="button"
        onClick={() => onChange([...lines, emptyFormulaLine(commonUnit)])}
        style={{
          ...primaryBtn,
          height: 32,
          background: 'transparent',
          color: colors.text,
          border: `1px solid ${colors.inputBorder}`,
          justifySelf: 'start',
        }}
      >
        <Plus size={14} /> Agregar componente
      </button>
    </div>
  );
};

const FormulaKindPanel = ({
  kind,
  formulas,
  statusFilter,
  onStatusFilter,
  onCreate,
  onEdit,
  onOpen,
}) => {
  const ui = useUIColors();
  const isColor = kind === FORMULA_KIND_COLOR;
  const rows = useMemo(
    () =>
      formulas
        .filter((row) => normalizeFormulaKind(row) === kind)
        .filter((row) => statusFilter === 'all' || row.status === statusFilter)
        .map((row) => ({
          ...row,
          components: Array.isArray(row.lines) ? row.lines.length : 0,
        })),
    [formulas, kind, statusFilter]
  );

  const columns = isColor
    ? [
        { key: 'Código', sortKey: 'code' },
        { key: 'Nombre', sortKey: 'name' },
        { key: 'Color', sortKey: 'color_code' },
        { key: 'Base', sortKey: 'base_formula_code' },
        { key: 'Versión', sortKey: 'version', align: 'right' },
        { key: 'Rendimiento', sortKey: 'yield_qty', align: 'right' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
        { key: 'Actualizada', sortKey: 'updated_at' },
        { key: '', sortKey: null, align: 'right' },
      ]
    : [
        { key: 'Código', sortKey: 'code' },
        { key: 'Nombre', sortKey: 'name' },
        { key: 'Versión', sortKey: 'version', align: 'right' },
        { key: 'Rendimiento', sortKey: 'yield_qty', align: 'right' },
        { key: 'Componentes', sortKey: 'components', align: 'right' },
        { key: 'Estado', sortKey: 'status', align: 'center' },
        { key: 'Actualizada', sortKey: 'updated_at' },
        { key: '', sortKey: null, align: 'right' },
      ];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <select
          value={statusFilter}
          onChange={(event) => onStatusFilter(event.target.value)}
          style={{
            height: 36,
            borderRadius: 8,
            border: `1px solid ${ui.inputBorder}`,
            background: ui.inputBg,
            color: ui.text,
            padding: '0 10px',
            fontSize: 13,
          }}
        >
          <option value="all">Todos los estados</option>
          {getActiveFormulaStatuses().map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>
      <ProductionDataTable
        title={isColor ? 'Fórmulas de colores' : 'Fórmulas de bases'}
        subtitle={`${rows.length} fórmulas`}
        rows={rows}
        columns={columns}
        defaultSortKey="code"
        searchPlaceholder={
          isColor ? 'Buscar código, nombre, color, base…' : 'Buscar código, nombre, versión…'
        }
        onRowClick={onOpen}
        action={
          <button type="button" onClick={() => onCreate(kind)} style={primaryBtn}>
            <Plus size={14} /> {isColor ? 'Nuevo color' : 'Nueva base'}
          </button>
        }
        renderCells={(row, colors) => (
          <>
            {cell(<strong>{row.code}</strong>)}
            {cell(row.name || row.product_name || '—')}
            {isColor
              ? cell(row.color_code ? `${row.color_code} · ${row.color_name || ''}` : '—')
              : null}
            {isColor ? cell(row.base_formula_code || '—') : null}
            {cell(`v${row.version}`, 'right')}
            {cell(formatQty(row.yield_qty, row.yield_unit), 'right')}
            {isColor ? null : cell(row.components, 'right')}
            {cell(
              <ProductionStatusChip status={row.status} color={getFormulaStatusColor(row.status)} />,
              'center'
            )}
            {cell(formatDateEs(row.updated_at))}
            {cell(
              <button
                type="button"
                title="Editar"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row);
                }}
                style={{
                  height: 28,
                  width: 28,
                  borderRadius: 6,
                  border: `1px solid ${colors.border}`,
                  background: 'transparent',
                  color: colors.text,
                  cursor: 'pointer',
                }}
              >
                <Pencil size={13} />
              </button>,
              'right'
            )}
          </>
        )}
      />
    </div>
  );
};

const PaintFormulasWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { formulas, materials, persistFormulas } = useProductionStore();
  const catalog = useMemo(() => activeMaterials(materials), [materials]);
  const materialByCodeMap = useMemo(() => {
    const map = {};
    catalog.forEach((row) => {
      const code = String(row.code || '').trim();
      if (code) map[code] = row;
    });
    return map;
  }, [catalog]);
  const activeBases = useMemo(
    () =>
      formulas.filter(
        (row) => normalizeFormulaKind(row) === FORMULA_KIND_BASE && row.status === 'Activa'
      ),
    [formulas]
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyFormula());
  const [formError, setFormError] = useState('');
  const [detail, setDetail] = useState(null);
  const [catalogTick, setCatalogTick] = useState(0);
  const techParamSuggestions = useMemo(() => {
    void catalogTick;
    return getTechParamSuggestions();
  }, [catalogTick]);

  const formKind = normalizeFormulaKind(formData);
  const isColorForm = formKind === FORMULA_KIND_COLOR;

  const openCreate = (kind) => {
    const blank = emptyFormula(kind);
    const generated = nextFormulaCode(formulas, kind);
    setFormData({
      ...blank,
      code: generated.code,
      version: generated.version,
    });
    setIsEditing(false);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const openEdit = (row) => {
    setFormData({
      ...emptyFormula(normalizeFormulaKind(row)),
      ...row,
      name: row.name || row.product_name || '',
      base_formula_id: row.base_formula_id ? String(row.base_formula_id) : '',
      tech_params: Array.isArray(row.tech_params) ? row.tech_params : [],
      lines:
        Array.isArray(row.lines) && row.lines.length
          ? row.lines
          : [emptyFormulaLine(row.component_unit || 'kg')],
    });
    setIsEditing(true);
    setFormError('');
    setShowForm(true);
    setDetail(null);
  };

  const handleChange = (key, value) => {
    setFormData((current) => {
      const next = { ...current, [key]: value };
      if (key === 'product_code') {
        const option = PAINT_PRODUCT_OPTIONS.find((item) => item.value === value);
        next.product_name = option ? option.label.replace(/^[A-Z0-9]+\s·\s/, '') : '';
      }
      if (key === 'base_formula_id') {
        const base = activeBases.find((row) => String(row.id) === String(value));
        next.base_formula_code = base?.code || '';
        if (base) {
          const unit = current.component_unit || 'kg';
          const rest = (current.lines || []).filter((line) => line.kind !== 'Base');
          next.lines = [
            {
              item_code: base.code,
              item_name: base.name || base.product_name,
              kind: 'Base',
              quantity: current.yield_qty || 100,
              unit,
              scrap_pct: 1,
            },
            ...(rest.length ? rest : [emptyFormulaLine(unit)]),
          ];
        }
      }
      return next;
    });
  };

  const persist = (status) => {
    const name = String(formData.name || '').trim();
    const code = String(formData.code || '').trim().toUpperCase();
    const lines = (formData.lines || []).filter((line) => line.item_code && Number(line.quantity) > 0);
    const techParams = sanitizeTechParams(formData.tech_params);
    const techError = techParamRangeError(techParams);
    if (!name) {
      setFormError('Indica el nombre de la fórmula.');
      return false;
    }
    if (!code) {
      setFormError('Indica el código de la fórmula.');
      return false;
    }
    if (isColorForm && !formData.base_formula_id) {
      setFormError('Selecciona la fórmula de base sobre la que se tintea el color.');
      return false;
    }
    if (isColorForm && !String(formData.color_code || formData.color_name || '').trim()) {
      setFormError('Indica el código o el nombre del color.');
      return false;
    }
    if (!lines.length) {
      setFormError('Agrega al menos un componente con cantidad.');
      return false;
    }
    if (techError) {
      setFormError(techError);
      return false;
    }
    const generated = nextFormulaCode(
      formulas.filter((row) => row.id !== formData.id),
      formKind
    );
    const version = isEditing && formData.version ? formData.version : generated.version;
    const base = activeBases.find((row) => String(row.id) === String(formData.base_formula_id));
    const commonUnit = String(formData.component_unit || 'kg').trim() || 'kg';
    const payload = {
      ...formData,
      formula_kind: formKind,
      code,
      name,
      version,
      product_code: String(formData.product_code || '').trim(),
      product_name: formData.product_name || productLabel(formData.product_code) || '',
      color_code: isColorForm ? String(formData.color_code || '').trim() : '',
      color_name: isColorForm ? String(formData.color_name || '').trim() : '',
      base_formula_id: isColorForm ? Number(formData.base_formula_id) || null : null,
      base_formula_code: isColorForm ? base?.code || formData.base_formula_code || '' : '',
      yield_qty: Number(formData.yield_qty) || 0,
      component_unit: commonUnit,
      cost_basis: String(formData.cost_basis || 'L') === 'gl' ? 'gl' : 'L',
      tech_params: techParams,
      status,
      lines: lines.map((line) => ({ ...line, unit: commonUnit })),
      updated_at: new Date().toISOString().slice(0, 10),
    };

    persistFormulas((current) => {
      let next = current;
      if (status === 'Activa') {
        next = next.map((row) => {
          const sameFamily =
            normalizeFormulaKind(row) === formKind &&
            String(row.name || '').trim().toLowerCase() === name.toLowerCase() &&
            row.status === 'Activa' &&
            row.id !== formData.id;
          const sameColor =
            !isColorForm ||
            String(row.color_code || '').trim().toLowerCase() ===
              String(payload.color_code).toLowerCase();
          return sameFamily && sameColor ? { ...row, status: 'Obsoleta' } : row;
        });
      }
      if (isEditing && formData.id) {
        return next.map((row) => (row.id === formData.id ? { ...row, ...payload } : row));
      }
      const id = next.reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
      return [...next, { ...payload, id }];
    });
    setShowForm(false);
    return true;
  };

  const sections = [
    {
      key: 'header',
      title: isColorForm ? 'Identidad, color y rendimiento' : 'Identidad y rendimiento',
      columns: 2,
      fields: [
        {
          key: 'code',
          label: 'Código de fórmula',
          placeholder: 'FO-B-001',
        },
        {
          key: 'name',
          label: 'Nombre de fórmula',
          placeholder: 'Ej. Base epóxica estándar',
        },
        {
          key: 'product_code',
          label: 'Producto T-COAT (opcional)',
          type: 'select',
          options: [
            { value: '', label: 'Sin asociar aún' },
            ...PAINT_PRODUCT_OPTIONS,
          ],
        },
        { key: 'version', label: 'Versión', type: 'number' },
        ...(isColorForm
          ? [
              { key: 'color_code', label: 'Código de color', placeholder: 'RAL 7035 / SW 7008' },
              { key: 'color_name', label: 'Nombre del color' },
              {
                key: 'base_formula_id',
                label: 'Fórmula de base',
                type: 'select',
                options: [
                  { value: '', label: 'Seleccionar base activa' },
                  ...activeBases.map((base) => ({
                    value: String(base.id),
                    label: `${base.code} · ${base.name || base.product_name}`,
                  })),
                ],
              },
            ]
          : []),
        { key: 'yield_qty', label: 'Rendimiento', type: 'number', step: '0.1' },
        {
          key: 'yield_unit',
          label: 'Unidad de rendimiento',
          type: 'select',
          options: [
            { value: 'L', label: 'L' },
            { value: 'kg', label: 'kg' },
          ],
        },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
    {
      key: 'tech',
      title: 'Parámetros técnicos',
      columns: 1,
      fields: [
        {
          key: 'tech_params',
          type: 'custom',
          fullWidth: true,
          render: ({ formData: data, onChange, colors, baseInputStyle }) => (
            <FormulaTechParamsTable
              rows={data.tech_params || []}
              onChange={onChange}
              colors={colors}
              baseInputStyle={baseInputStyle}
              suggestions={techParamSuggestions}
              onSuggestionsChange={() => setCatalogTick((n) => n + 1)}
            />
          ),
        },
      ],
    },
    {
      key: 'bom',
      title: isColorForm
        ? 'Componentes (base + pigmentos / colorantes)'
        : 'Componentes de la formulación',
      columns: 1,
      fields: [
        {
          key: 'lines',
          type: 'custom',
          fullWidth: true,
          render: ({ value, onChange, colors, baseInputStyle }) => (
            <BomEditor
              value={value}
              onChange={onChange}
              materials={catalog}
              bases={activeBases}
              showBases={isColorForm}
              formula={formData}
              onFormulaMetaChange={handleChange}
              materialByCodeMap={materialByCodeMap}
              colors={colors}
              baseInputStyle={baseInputStyle}
            />
          ),
        },
      ],
    },
  ];

  const tabs = [
    {
      key: FORMULA_KIND_BASE,
      label: FORMULA_KIND_LABELS.base,
      icon: <Layers size={16} />,
      content: (
        <FormulaKindPanel
          kind={FORMULA_KIND_BASE}
          formulas={formulas}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onCreate={openCreate}
          onEdit={openEdit}
          onOpen={setDetail}
        />
      ),
    },
    {
      key: FORMULA_KIND_COLOR,
      label: FORMULA_KIND_LABELS.color,
      icon: <Palette size={16} />,
      content: (
        <FormulaKindPanel
          kind={FORMULA_KIND_COLOR}
          formulas={formulas}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onCreate={openCreate}
          onEdit={openEdit}
          onOpen={setDetail}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Fórmulas
        </Heading>
        <p style={{ margin: 0, maxWidth: 760, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Cada fórmula tiene código y nombre propios (sin exigir producto). Define parámetros
          técnicos, componentes con % m/m y costos normalizados por litro o galón cuando hay vínculo
          a materias primas de adquisiciones.
        </p>
      </div>

      <TabNavigation tabs={tabs} defaultTab={FORMULA_KIND_BASE} isDarkMode={isDarkMode} />

      <FormDrawer
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSave={() => persist(formData.status === 'Activa' ? 'Activa' : 'Borrador')}
        isDarkMode={isDarkMode}
        title={
          isEditing
            ? `Editar fórmula de ${formulaKindLabel(formKind).toLowerCase()}`
            : `Nueva fórmula de ${formulaKindLabel(formKind).toLowerCase()}`
        }
        subtitle={
          formData.code
            ? `${formData.code}${formData.name ? ` · ${formData.name}` : ''}`
            : isColorForm
              ? 'Tintado sobre base'
              : 'Formulación de producción'
        }
        sections={sections}
        formData={formData}
        onChange={handleChange}
        formError={formError}
        width={860}
        footerActions={[
          { key: 'cancel', label: 'Cancelar', onClick: () => setShowForm(false) },
          {
            key: 'draft',
            label: 'Guardar borrador',
            onClick: () => persist('Borrador'),
          },
          {
            key: 'active',
            label: isColorForm ? 'Activar color' : 'Activar base',
            variant: 'success',
            onClick: () => persist('Activa'),
          },
        ]}
      />

      <PaintFormulaDetailModal
        isOpen={Boolean(detail)}
        onClose={() => setDetail(null)}
        formula={detail}
        isDarkMode={isDarkMode}
        ui={ui}
        materials={catalog}
        onEdit={openEdit}
      />
    </div>
  );
};

export default PaintFormulasWorkspace;
